import { useState } from "react";
import {
  Loader2,
  Plus,
  X,
  Save,
  Check,
  Trash2,
  Phone,
  Mail,
  Calendar,
  StickyNote,
  AlertTriangle,
  Clock,
  CalendarClock,
  ListTodo,
} from "lucide-react";
import {
  useAgenda,
  useCrearTarea,
  useCompletarTarea,
  useEliminarTarea,
  useContactos,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Tarea } from "../types";

function eur(n: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

const TIPO_ICON: Record<string, typeof Phone> = {
  llamada: Phone,
  email: Mail,
  reunion: Calendar,
  nota: StickyNote,
  tarea: ListTodo,
};

export function AgendaPage() {
  const { emisorId } = useMarketingStore();
  const { data, isLoading } = useAgenda(emisorId);
  const { data: contactosData } = useContactos(emisorId);
  const contactos = contactosData?.data;

  const crearMut = useCrearTarea();
  const completarMut = useCompletarTarea();
  const eliminarMut = useEliminarTarea();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    asunto: "",
    tipo: "tarea",
    fecha: new Date().toISOString().slice(0, 10),
    contacto_id: 0,
  });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId || !form.asunto) return;
    await crearMut.mutateAsync({
      emisorId,
      payload: {
        asunto: form.asunto,
        tipo: form.tipo,
        fecha: form.fecha,
        contacto_id: form.contacto_id || undefined,
      },
    });
    setCreating(false);
    setForm({ asunto: "", tipo: "tarea", fecha: new Date().toISOString().slice(0, 10), contacto_id: 0 });
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Agenda comercial: tus tareas y seguimientos."
        right={
          <button
            onClick={() => setCreating(true)}
            disabled={!emisorId}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition self-end disabled:opacity-50"
          >
            <Plus size={16} /> Nueva tarea
          </button>
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {data && (
        <>
          {/* Resumen */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
            <ResumenCard label="Vencidas" value={data.resumen.vencidas} icon={AlertTriangle} accent="rose" />
            <ResumenCard label="Para hoy" value={data.resumen.hoy} icon={Clock} accent="amber" />
            <ResumenCard label="Próximas" value={data.resumen.proximas} icon={CalendarClock} accent="sky" />
            <ResumenCard label="Estancadas" value={data.estancadas.length} icon={Clock} accent="slate" />
          </div>

          <div className="flex-1 min-h-0 overflow-auto grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Tareas */}
            <div className="lg:col-span-2 space-y-4">
              <TaskGroup
                title="Vencidas"
                tareas={data.vencidas}
                color="rose"
                onComplete={(id) => completarMut.mutate(id)}
                onDelete={(id) => eliminarMut.mutate(id)}
              />
              <TaskGroup
                title="Para hoy"
                tareas={data.hoy}
                color="amber"
                onComplete={(id) => completarMut.mutate(id)}
                onDelete={(id) => eliminarMut.mutate(id)}
              />
              <TaskGroup
                title="Próximas"
                tareas={data.proximas}
                color="sky"
                onComplete={(id) => completarMut.mutate(id)}
                onDelete={(id) => eliminarMut.mutate(id)}
              />
              {data.resumen.total_pendientes === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                  <ListTodo size={32} className="mx-auto mb-2 text-slate-300" />
                  <p className="font-bold text-slate-700">¡Todo al día!</p>
                  <p className="text-sm text-slate-400">No tienes tareas pendientes.</p>
                </div>
              )}
            </div>

            {/* Oportunidades estancadas */}
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5 self-start">
              <h2 className="font-black text-slate-900 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-600" /> Oportunidades estancadas
              </h2>
              <p className="text-xs text-slate-400 mb-3">Sin movimiento en más de 30 días.</p>
              {data.estancadas.length === 0 ? (
                <p className="text-sm text-slate-400">Ninguna. ¡Buen ritmo!</p>
              ) : (
                <div className="space-y-2">
                  {data.estancadas.map((o) => (
                    <div key={o.id} className="rounded-xl border border-slate-200 p-3">
                      <div className="font-bold text-sm text-slate-900 truncate">{o.titulo}</div>
                      <div className="text-xs text-slate-500 truncate">{o.contacto_nombre}</div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="font-mono font-bold text-slate-900 text-sm">{eur(o.valor)}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          {o.dias_sin_movimiento}d sin mover
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nueva tarea</h2>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Asunto *</label>
                <input
                  required
                  type="text"
                  value={form.asunto}
                  onChange={(e) => setForm({ ...form, asunto: e.target.value })}
                  placeholder="ej: Llamar a Empresa X"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo</label>
                  <select
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="tarea">Tarea</option>
                    <option value="llamada">Llamada</option>
                    <option value="email">Email</option>
                    <option value="reunion">Reunión</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fecha</label>
                  <input
                    type="date"
                    value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Contacto (opcional)</label>
                <select
                  value={form.contacto_id}
                  onChange={(e) => setForm({ ...form, contacto_id: Number(e.target.value) })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value={0}>— ninguno —</option>
                  {contactos?.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}{c.empresa && c.empresa !== c.nombre ? ` · ${c.empresa}` : ""}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">
                Cancelar
              </button>
              <button type="submit" disabled={crearMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                <Save size={14} /> Crear
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ResumenCard({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  icon: typeof Clock;
  accent: "rose" | "amber" | "sky" | "slate";
}) {
  const cls = {
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    slate: "border-slate-200 bg-slate-50 text-slate-600",
  }[accent];
  return (
    <div className={`rounded-2xl border p-4 ${cls}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-black text-slate-900">{value}</div>
          <div className="text-xs font-bold uppercase tracking-wider">{label}</div>
        </div>
        <Icon size={20} />
      </div>
    </div>
  );
}

function TaskGroup({
  title,
  tareas,
  color,
  onComplete,
  onDelete,
}: {
  title: string;
  tareas: Tarea[];
  color: "rose" | "amber" | "sky";
  onComplete: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  if (tareas.length === 0) return null;
  const dot = { rose: "bg-rose-500", amber: "bg-amber-500", sky: "bg-sky-500" }[color];

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden">
      <header className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <h3 className="font-black text-slate-900 text-sm">{title}</h3>
        <span className="text-xs text-slate-400">({tareas.length})</span>
      </header>
      <div className="divide-y divide-slate-100">
        {tareas.map((t) => {
          const Icon = TIPO_ICON[t.tipo] ?? ListTodo;
          return (
            <div key={t.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/60 group">
              <button
                onClick={() => onComplete(t.id)}
                title="Completar"
                className="w-6 h-6 rounded-full border-2 border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 grid place-items-center text-transparent hover:text-emerald-600 transition shrink-0"
              >
                <Check size={13} />
              </button>
              <div className="w-7 h-7 rounded-lg bg-slate-100 grid place-items-center text-slate-500 shrink-0">
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-slate-900 truncate">{t.asunto}</div>
                <div className="text-xs text-slate-500 truncate">
                  {t.contacto_nombre ?? "Sin contacto"}
                  {t.fecha && ` · ${new Date(t.fecha).toLocaleDateString("es-ES")}`}
                </div>
              </div>
              <button
                onClick={() => onDelete(t.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 shrink-0"
              >
                <Trash2 size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
